import React, {useEffect, useState, useRef} from 'react'
import { useNavigate } from 'react-router-dom';
// components
import WLChart from '../../Systems/WinLossChart';
import WinPerChart from '../../Systems/WinPerChart';
import ProfitChart from '../../Systems/ProfitChart';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import SystemBets from './SystemBets';
import { PrimaryBtn } from '../../Assets/Buttons';
// utils
import { formatBetName } from '../../../utils/systems/systemUtils';
// icons
import { 
    ShareIcon, 
    PencilSquareIcon, 
    TrashIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    HeartIcon,
    Cog6ToothIcon,
    ExclamationTriangleIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const SystemDisp = (props) => {
    const [system, setSystem] = useState({});
    const sznWL = props.sznWL;
    const pageType = props.pageType;
    const [showEllipseDrop, setShowEllipseDrop] = useState(false);
    const [showPubModal, setShowPubModal] = useState(false);
    const [showDelModal, setShowDelModal] = useState(false);
    const [activeView, setActiveView] = useState("overview");
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    useEffect(()=>{
        setSystem(props.system);
    },[props]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowEllipseDrop(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Calculate win percentage for display
    const winPercentage = system.roi !== "NaN" ? (system.w/(system.w+system.l)*100).toFixed(1) : 0;

    // Convert American odds to decimal odds
    const americanToDecimal = (americanOdds) => {
        if (americanOdds > 0) {
            return (americanOdds / 100) + 1;
        } else {
            return (100 / Math.abs(americanOdds)) + 1;
        }
    };

    // Convert decimal odds to American odds
    const decimalToAmerican = (decimalOdds) => {
        if (decimalOdds >= 2) {
            return ((decimalOdds - 1) * 100).toFixed(0);
        } else {
            return (-100 / (decimalOdds - 1)).toFixed(0);
        }
    };

    // Calculate average odds if available
    const avgOdds = system.matches ? 
        Number(decimalToAmerican(
            Object.values(system.matches)
                .flat()
                .reduce((sum, match) => sum + americanToDecimal(parseFloat(match.bet.odds)), 0) / 
                Object.values(system.matches).flat().length
        ))
        : null;

    // Calculate current streak
    const calculateStreak = () => {
        if (!system.matches) return { count: 0, type: '-' };
        
        const bets = Object.values(system.matches).flat().reverse();
        if (bets.length === 0) return { count: 0, type: '-' };
        
        let count = 1;
        const currentType = bets[0].bet.result;
        
        for (let i = 1; i < bets.length; i++) {
            if (bets[i].bet.result === currentType) {
                count++;
            } else {
                break;
            }
        }
        
        return { count, type: currentType };
    };

    const streak = calculateStreak();

    // Calculate units (assuming 1 unit per bet)
    const calculateUnits = () => {
        if (!system.matches) return 0;
        
        return Object.values(system.matches)
            .flat()
            .reduce((total, match) => {
                const odds = parseFloat(match.bet.odds);
                if (match.bet.result === 'W') {
                    return total + (odds > 0 ? odds/100 : -100/odds);
                } else if (match.bet.result === 'L') {
                    return total - 1;
                }
                return total; // Push
            }, 0).toFixed(1);
    };

    const units = calculateUnits();

    // open menu on click of ellipsis
    const handleEllipseClick = () => {
        setShowEllipseDrop(!showEllipseDrop);
    };

    // Show publish system modal
    const handlePublishClick = () => {
        setShowPubModal(!showPubModal);
    };

    // function for publish completion and modal closure
    const closePublishedModal = () =>{
        props.setModalStep('submission');
        setShowPubModal(false);
    };

    // function to show delete system modal 
    const handleDeleteClick = () => {
        setShowDelModal(!showDelModal);
    };

    // function to handle edit click 
    const handleEditClick = () => {
        props.editSystem();
    };

    let pubModal = (
        <Dialog open={showPubModal} onClose={() => setShowPubModal(false)} className="relative z-10">
            <DialogBackdrop
                className="fixed inset-0 bg-gray-500/75 transition-opacity"
                aria-hidden="true"
                onClick={() => setShowPubModal(false)}
            />
            
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                                    Publish System
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Are you sure you would like to publish this system? Once public you will not be able to modify or delete it.
                                    </p>
                                </div>
                        </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md bg-brand-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-700 sm:ml-3 sm:w-auto"
                                onClick={() => props.publishSystem(system)}
                            >
                                Publish
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                                onClick={() => setShowPubModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                                </div>
                            </div>
        </Dialog>
    );

    let delModal = (
        <Dialog open={showDelModal} onClose={() => setShowDelModal(false)} className="relative z-10">
            <DialogBackdrop
                className="fixed inset-0 bg-gray-500/75 transition-opacity"
                aria-hidden="true"
                onClick={() => setShowDelModal(false)}
            />
            
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                                    Delete System
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Are you sure you want to delete this system? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                            </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md bg-brand-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-700 sm:ml-3 sm:w-auto"
                                onClick={() => props.deleteSystem()}
                            >
                                Delete
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                                onClick={() => setShowDelModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                    </div>
                    </div>
        </Dialog>
                );

    return (
        <div className="min-h-screen bg-white dark:bg-brand-dark-900">
            {showPubModal && pubModal}
            {showDelModal && delModal}
            
            <div className="h-screen flex flex-col">
                {/* Header with system name and actions */}
                <div className="sticky top-0 z-10 bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-8 py-4">
                    <div className="max-w-[1920px] mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {pageType !== "edit" && (
                                <>
                                    <button 
                                        onClick={() => navigate('/systems')}
                                        className="sm:hidden text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white"
                                    >
                                        <ArrowLeftIcon className="h-5 w-5" />
                                    </button>
                                    <div className="hidden sm:block">
                                        <PrimaryBtn
                                            onClick={() => navigate('/systems')}
                                            text="Back to Systems"
                                        />
                                    </div>
                                </>
                            )}
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {system.name} - {system.sport}
                            </h1>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                            {(pageType==="existing" && props.sysType ==="private") && (
                                <div className="relative" ref={dropdownRef}>
                                    <Cog6ToothIcon 
                                        className="h-6 w-6 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white cursor-pointer" 
                                        onClick={handleEllipseClick}
                                    />
                                    {showEllipseDrop && (
                                        <div className="absolute right-0 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
                                            <div className="py-1" role="menu">
                                                <button onClick={handlePublishClick} className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                                    <ShareIcon className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-300" />
                                                    Publish System
                                                </button>
                                                <button onClick={handleEditClick} className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                                    <PencilSquareIcon className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-300" />
                                                    Edit
                                                </button>
                                                <button onClick={handleDeleteClick} className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                                    <TrashIcon className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-300" />
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {(pageType==="existing" && props.sysType ==="public") && (
                                <button 
                                    onClick={props.handleSystemFollow}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-red-600 hover:bg-brand-red-700"
                                >
                                    <HeartIcon className="h-4 w-4 mr-2" />
                                    Follow
                                </button>
                            )}
                            {(pageType==="existing" && props.sysType ==="followed") && (
                                <HeartIconSolid className="h-6 w-6 text-brand-red-600" />
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile View Selector */}
                <div className="bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 md:hidden">
                    <div className="flex gap-2 py-2">
                        <button 
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeView === "overview" 
                                    ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            }`}
                            onClick={() => setActiveView("overview")}
                        >
                            <span>Overview</span>
                        </button>
                        <button 
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeView === "charts" 
                                    ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            }`}
                            onClick={() => setActiveView("charts")}
                        >
                            <span>Charts</span>
                        </button>
                        <button 
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeView === "bets" 
                                    ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white" 
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            }`}
                            onClick={() => setActiveView("bets")}
                        >
                            <span>Bets</span>
                        </button>
                </div>
            </div>

                {/* Metrics Row */}
                <div className="hidden md:block bg-white dark:bg-brand-dark-900 px-4 lg:px-8 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="max-w-[1920px] mx-auto grid grid-cols-6 gap-4">
                        {/* Win Rate with Donut */}
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Win Rate</h3>
                            <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                {winPercentage}%
                            </span>
                        </div>

                        {/* Bet Type */}
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Bet Type</h3>
                            <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                {system.bet ? formatBetName(system.bet) : '-'}
                            </span>
                        </div>

                        {/* ROI */}
                            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">ROI</h3>
                            <div className="flex items-center">
                                {system.roi > 0 ? (
                                    <ArrowTrendingUpIcon className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-4 w-4 mr-2 text-red-600 dark:text-red-400" />
                                )}
                                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {isNaN(system.roi) ? '0%' : `${system.roi}%`}
                                    </span>
                                </div>
                            </div>

                        {/* Streak */}
                            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Streak</h3>
                            <span className={`text-xl font-semibold ${
                                        streak.type === 'W' ? 'text-green-600 dark:text-green-400' : 
                                        streak.type === 'L' ? 'text-red-600 dark:text-red-400' : 
                                        'text-gray-900 dark:text-white'
                                    }`}>
                                        {streak.count}{streak.type}
                                    </span>
                            </div>

                        {/* Units */}
                            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Units</h3>
                            <span className={`text-xl font-semibold ${
                                        units > 0 ? 'text-green-600 dark:text-green-400' : 
                                        units < 0 ? 'text-red-600 dark:text-red-400' : 
                                        'text-gray-900 dark:text-white'
                                    }`}>
                                        {units > 0 ? '+' : ''}{units}
                                    </span>
                        </div>

                        {/* Average Odds */}
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Average Odds</h3>
                            <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                {avgOdds ? (avgOdds > 0 ? '+' : '') + avgOdds : '-'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 p-4 lg:px-8 pb-8 overflow-y-auto">
                    <div className="max-w-[1920px] mx-auto lg:px-0">
                        {/* Desktop Layout */}
                        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {/* Left Column - Info and Bets */}
                            <div className="md:col-span-1 lg:col-span-2 flex flex-col gap-4">
                                {/* System Info Card */}
                                <div className="rounded-lg bg-white dark:bg-gray-800 shadow">
                                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-base font-medium text-gray-900 dark:text-white">Criteria</h3>
                                    </div>
                                    <div className="px-4 py-2">
                                        <div className="space-y-2">
                                            <div>
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {system && system.criteria && system.criteria.map((crit, i) => (
                                                        <div 
                                                            key={i}
                                                            className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300"
                                                        >
                                                            {crit.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bets Card */}
                                <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex-1">
                                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-base font-medium text-gray-900 dark:text-white">System Bets</h3>
                                    </div>
                                    <div className="h-[calc(100vh-400px)] overflow-y-auto">
                                        <div className="px-4">
                                            <SystemBets matches={system.matches} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Charts */}
                            <div className="md:col-span-1 lg:col-span-3">
                                {system.roi !== "NaN" && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Win/Loss Chart Card */}
                                        <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                                <h3 className="text-base font-medium text-gray-900 dark:text-white">Win/Loss History</h3>
                                            </div>
                                            <div className="p-2">
                                                <div className="aspect-[16/9] w-full">
                                                    <WLChart sznWL={sznWL} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Win Percentage Chart Card */}
                                        <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                                <h3 className="text-base font-medium text-gray-900 dark:text-white">Win Percentage</h3>
                                            </div>
                                            <div className="p-2">
                                                <div className="aspect-[16/9] w-full">
                                                    <WinPerChart sznWL={sznWL} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Profit Chart Card */}
                                        <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                                <h3 className="text-base font-medium text-gray-900 dark:text-white">Profit History</h3>
                                            </div>
                                            <div className="p-2">
                                                <div className="aspect-[16/9] w-full">
                                                    <ProfitChart sznWL={sznWL} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mobile Layout */}
                        <div className="md:hidden">
                            {/* Overview View */}
                            {activeView === "overview" && (
                                <div className="space-y-4">
                                    {/* System Info Card */}
                                    <div className="rounded-lg bg-white dark:bg-gray-800 shadow">
                                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <h3 className="text-base font-medium text-gray-900 dark:text-white">Criteria</h3>
                                        </div>
                                        <div className="px-4 py-2">
                                            <div className="space-y-2">
                                                <div>
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        {system && system.criteria && system.criteria.map((crit, i) => (
                                                            <div 
                                                                key={i}
                                                                className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300"
                                                            >
                                                                {crit.label}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Metrics Grid */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Win Rate */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">Win Rate</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                                    {winPercentage}%
                                                </span>
                                            </div>
                                        </div>

                                        {/* Bet Type */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">Bet Type</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                                    {system.bet}
                                                </span>
                                            </div>
                                        </div>

                                        {/* ROI */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">ROI</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                {system.roi > 0 ? (
                                                    <ArrowTrendingUpIcon className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                                                ) : (
                                                    <ArrowTrendingDownIcon className="h-4 w-4 mr-2 text-red-600 dark:text-red-400" />
                                                )}
                                                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                                    {isNaN(system.roi) ? '0%' : `${system.roi}%`}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Streak */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">Streak</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                <span className={`text-xl font-semibold ${
                                                    streak.type === 'W' ? 'text-green-600 dark:text-green-400' : 
                                                    streak.type === 'L' ? 'text-red-600 dark:text-red-400' : 
                                                    'text-gray-900 dark:text-white'
                                                }`}>
                                                    {streak.count}{streak.type}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Units */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">Units</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                <span className={`text-xl font-semibold ${
                                                    units > 0 ? 'text-green-600 dark:text-green-400' : 
                                                    units < 0 ? 'text-red-600 dark:text-red-400' : 
                                                    'text-gray-900 dark:text-white'
                                                }`}>
                                                    {units > 0 ? '+' : ''}{units}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Average Odds */}
                                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 text-center sm:text-left">Average Odds</h3>
                                            <div className="flex items-center justify-center sm:justify-start">
                                                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                                                    {avgOdds ? (avgOdds > 0 ? '+' : '') + avgOdds : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Charts View */}
                            {activeView === "charts" && system.roi !== "NaN" && (
                                <div className="space-y-4">
                                    {/* Win/Loss Chart Card */}
                                    <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <h3 className="text-base font-medium text-gray-900 dark:text-white">Win/Loss History</h3>
                                        </div>
                                        <div className="p-2">
                                            <div className="aspect-[16/9] w-full">
                                                <WLChart sznWL={sznWL} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Win Percentage Chart Card */}
                                    <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <h3 className="text-base font-medium text-gray-900 dark:text-white">Win Percentage</h3>
                                        </div>
                                        <div className="p-2">
                                            <div className="aspect-[16/9] w-full">
                                                <WinPerChart sznWL={sznWL} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Profit Chart Card */}
                                    <div className="rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col">
                                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <h3 className="text-base font-medium text-gray-900 dark:text-white">Profit History</h3>
                                        </div>
                                        <div className="p-2">
                                            <div className="aspect-[16/9] w-full">
                                                <ProfitChart sznWL={sznWL} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Bets View */}
                            {activeView === "bets" && (
                                <div className="rounded-lg bg-white dark:bg-gray-800 shadow">
                                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-base font-medium text-gray-900 dark:text-white">System Bets</h3>
                                    </div>
                                    <div className="h-[calc(100vh-250px)] overflow-y-auto">
                                        <div className="px-4">
                                            <SystemBets matches={system.matches} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default SystemDisp